import { useState, useEffect } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { addUser, CreateUserPayload } from 'store/userSlice';
import { fetchCompanies } from 'store/companySlice';
import { fetchDepartments } from 'store/departmentmanagementSlice';
import { getAllRoles } from 'constants/roles';
import { getEffectiveCompanyContext } from 'api/contextAPI';
import { showSuccess } from 'utils/errorHandler';

// Password validation function

interface PasswordRequirements {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  strength: number;
}

const validatePassword = (password: string): PasswordRequirements => {
  const requirements = {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  };

  let strength = 0;
  const checks = Object.values(requirements);
  const passedChecks = checks.filter(Boolean).length;
  strength = (passedChecks / checks.length) * 100;

  return {
    ...requirements,
    strength
  };
};

interface CreateUserModalProps {
  show: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
}

interface ApiError {
  message?: string;
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const CreateUserModal = ({
  show,
  handleClose,
  onSuccess
}: CreateUserModalProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { data: companies, loading: companiesLoading } = useSelector(
    (state: RootState) => state.companies
  );
  const { data: departments, loading: departmentsLoading } = useSelector(
    (state: RootState) => state.department
  );
  const { role: userRole } = useSelector((state: RootState) => state.auth);

  const [effectiveCompanyId, setEffectiveCompanyId] = useState<number | null>(
    null
  );
  const [formData, setFormData] = useState<CreateUserPayload>({
    name: '',
    email: '',
    password: '',
    role_id: 0,
    company_id: 0,
    department_id: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredDepartments, setFilteredDepartments] = useState(departments);

  const [showPassword, setShowPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] =
    useState<PasswordRequirements>({
      hasMinLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false,
      strength: 0
    });

  const isSuperAdmin = userRole === 'super_user';

  // Fetch effective company from context API
  useEffect(() => {
    const fetchEffectiveCompany = async () => {
      try {
        const context = await getEffectiveCompanyContext();
        const companyId = context.company_id || null;
        setEffectiveCompanyId(companyId);

        // If not super admin, set the company_id in form data
        if (!isSuperAdmin && companyId) {
          setFormData(prev => ({ ...prev, company_id: companyId }));
        }
      } catch (error) {
        console.error('Failed to fetch effective company:', error);
      }
    };

    if (show) {
      fetchEffectiveCompany();
      dispatch(fetchCompanies());
      dispatch(fetchDepartments());
    }
  }, [dispatch, show, isSuperAdmin]);

  useEffect(() => {
    if (formData.company_id) {
      setFilteredDepartments(
        departments.filter(dept => dept.company_id === formData.company_id)
      );
      setFormData(prev => ({ ...prev, department_id: 0 }));
    } else {
      setFilteredDepartments([]);
    }
  }, [formData.company_id, departments]);

  useEffect(() => {
    if (formData.password) {
      setPasswordRequirements(validatePassword(formData.password));
    } else {
      setPasswordRequirements({
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
        strength: 0
      });
    }
  }, [formData.password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!formData.password.trim()) {
      setError('Password is required');
      return;
    }

    const requirements = validatePassword(formData.password);
    const allRequirementsMet =
      requirements.hasMinLength &&
      requirements.hasUppercase &&
      requirements.hasLowercase &&
      requirements.hasNumber &&
      requirements.hasSpecialChar;

    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }

    if (!formData.role_id) {
      setError('Please select a role');
      return;
    }

    if (!formData.company_id) {
      setError('Please select a company');
      return;
    }

    if (!formData.department_id) {
      setError('Please select a department');
      return;
    }

    setLoading(true);
    try {
      await dispatch(addUser(formData)).unwrap();

      // Reset form data
      setFormData({
        name: '',
        email: '',
        password: '',
        role_id: 0,
        company_id: 0,
        department_id: 0
      });

      // Close modal first, then show success and refresh
      handleClose();
      showSuccess('User created successfully');
      onSuccess?.();
    } catch (err: unknown) {
      let errorMessage = 'Failed to create user';

      if (typeof err === 'object' && err !== null) {
        const apiError = err as ApiError;

        if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.response?.data?.detail) {
          errorMessage = apiError.response.data.detail;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      console.error('Failed to create user:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: Number(value)
    }));
  };

  const handleModalClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role_id: 0,
      company_id: 0,
      department_id: 0
    });
    setShowPassword(false);
    setError(null);
    handleClose();
  };

  const isLoading = companiesLoading || departmentsLoading;

  return (
    <Modal show={show} onHide={handleModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New User</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  placeholder="Enter full name"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  placeholder="Enter email address"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Password *</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Enter password"
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <FontAwesomeIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    className="text-body-tertiary fs-9 position-absolute"
                    style={{
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      zIndex: 10
                    }}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </div>

                {/* Password requirements display */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="d-flex flex-column gap-1 small">
                      <div className="d-flex align-items-center">
                        <span
                          className={`me-2 ${
                            passwordRequirements.hasUppercase
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {passwordRequirements.hasUppercase ? '✓' : '✗'}
                        </span>
                        <span>At least one uppercase letter (A-Z)</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className={`me-2 ${
                            passwordRequirements.hasLowercase
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {passwordRequirements.hasLowercase ? '✓' : '✗'}
                        </span>
                        <span>At least one lowercase letter (a-z)</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className={`me-2 ${
                            passwordRequirements.hasNumber
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {passwordRequirements.hasNumber ? '✓' : '✗'}
                        </span>
                        <span>At least one number (0-9)</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className={`me-2 ${
                            passwordRequirements.hasSpecialChar
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {passwordRequirements.hasSpecialChar ? '✓' : '✗'}
                        </span>
                        <span>
                          At least one special character (!@#$%^&* etc.)
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className={`me-2 ${
                            passwordRequirements.hasMinLength
                              ? 'text-success'
                              : 'text-danger'
                          }`}
                        >
                          {passwordRequirements.hasMinLength ? '✓' : '✗'}
                        </span>
                        <span>Minimum 8 characters</span>
                      </div>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Role *</Form.Label>
                <Form.Select
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleSelectChange}
                  required
                  disabled={loading}
                >
                  <option value={0}>Select Role</option>
                  {getAllRoles().map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Company *</Form.Label>
                <Form.Select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleSelectChange}
                  required
                  disabled={
                    isLoading ||
                    loading ||
                    (!isSuperAdmin && effectiveCompanyId !== null)
                  }
                >
                  <option value={0}>Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Form.Select>
                {companiesLoading && (
                  <Form.Text className="text-muted">
                    Loading companies...
                  </Form.Text>
                )}
                {!companiesLoading && companies.length === 0 && (
                  <Form.Text className="text-warning">
                    No companies available. Please create a company first.
                  </Form.Text>
                )}
                {!isSuperAdmin && effectiveCompanyId !== null && (
                  <Form.Text className="text-muted">
                    Company is set based on your account
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Department *</Form.Label>
                <Form.Select
                  name="department_id"
                  value={formData.department_id}
                  onChange={handleSelectChange}
                  required
                  disabled={!formData.company_id || isLoading || loading}
                >
                  <option value={0}>Select Department</option>
                  {filteredDepartments.map(department => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </Form.Select>
                {!formData.company_id && (
                  <Form.Text className="text-muted">
                    Please select a company first
                  </Form.Text>
                )}
                {formData.company_id &&
                  filteredDepartments.length === 0 &&
                  !departmentsLoading && (
                    <Form.Text className="text-warning">
                      No departments available for this company
                    </Form.Text>
                  )}
                {departmentsLoading && (
                  <Form.Text className="text-muted">
                    Loading departments...
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
